// src/components/LogEmailWidget.js
// Consultazione del registro degli invii email automatici.
//
// Serve a rispondere con un dato — e non con una supposizione — alla domanda
// "l'avviso è stato mandato, a chi e quando?". È la differenza tra dire "il sistema
// dovrebbe aver avvisato" e poterlo dimostrare.
//
// Il log lo scrivono solo le Cloud Functions: da qui è in sola lettura, e le regole
// Firestore impediscono a chiunque di modificarlo o cancellarlo. Un registro alterabile
// non dimostrerebbe nulla.
import React, { useEffect, useState, useMemo } from "react";
import { collection, query, where, onSnapshot, orderBy, limit } from "firebase/firestore";
import { db } from "../firebase";
import { useOrg } from "../contexts/OrgContext";
import { Mail, AlertTriangle, CheckCircle2 } from "lucide-react";

const TIPI = {
  attivita:               { label:"Attività",              emoji:"📋" },
  sottoattivita:          { label:"Sottoattività",         emoji:"📎" },
  contratto_manutenzione: { label:"Contratto manutenzione",emoji:"🔧" },
  test:                   { label:"Test configurazione",   emoji:"🧪" },
  generico:               { label:"Altro",                 emoji:"✉️" },
};

export default function LogEmailWidget() {
  const { effectiveOrgId } = useOrg();
  const [log, setLog] = useState([]);
  const [filtro, setFiltro] = useState("tutti");
  const [errore, setErrore] = useState(null);

  useEffect(() => {
    if (!effectiveOrgId) return;
    // Ultimi 200: il registro cresce ogni giorno, e per rispondere a "è stata mandata?"
    // servono i giorni recenti — non l'archivio completo caricato tutto in una volta.
    const q = query(collection(db,"logNotificheEmail"),
      where("orgId","==",effectiveOrgId), orderBy("inviataIl","desc"), limit(200));
    return onSnapshot(q,
      snap => { setLog(snap.docs.map(d=>({id:d.id,...d.data()}))); setErrore(null); },
      err => {
        console.error("Errore lettura log email:", err);
        setErrore(err?.code === "failed-precondition"
          ? "Manca un indice Firestore per questa vista — apri la console del browser (F12): troverai un link per crearlo in un click."
          : "Non riesco a leggere il registro invii in questo momento.");
      });
  }, [effectiveOrgId]);

  const filtrati = useMemo(() =>
    log.filter(l => filtro==="tutti" || (filtro==="falliti" ? l.esito==="fallita" : l.esito==="inviata")),
  [log, filtro]);

  const nFalliti = log.filter(l=>l.esito==="fallita").length;

  if (errore) {
    return <div style={{padding:16,fontSize:12.5,color:"#dc2626",background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:9}}>⚠️ {errore}</div>;
  }

  return (
    <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:16}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
        <Mail size={16} color="#7c3aed"/>
        <span style={{fontSize:13.5,fontWeight:800,color:"#1e1b4b"}}>Registro invii email automatici</span>
      </div>
      <p style={{fontSize:11.5,color:"#9ca3af",marginBottom:12}}>
        Ultimi 200 invii. Scritto solo dal server e non modificabile da nessuno — è ciò che
        permette di dimostrare che un avviso è davvero partito.
      </p>

      <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
        {[{k:"tutti",label:`Tutti (${log.length})`},
          {k:"inviati",label:`Inviate (${log.length-nFalliti})`},
          {k:"falliti",label:`Fallite (${nFalliti})`}].map(b=>(
          <button key={b.k} onClick={()=>setFiltro(b.k)}
            style={{padding:"5px 12px",borderRadius:8,fontSize:11.5,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",
              border:`1px solid ${filtro===b.k ? (b.k==="falliti"?"#fca5a5":"#7c3aed") : "#e5e7eb"}`,
              background: filtro===b.k ? (b.k==="falliti"?"#fef2f2":"#faf5ff") : "#fff",
              color: filtro===b.k ? (b.k==="falliti"?"#dc2626":"#6d28d9") : "#6b7280"}}>
            {b.label}
          </button>
        ))}
      </div>

      {nFalliti > 0 && filtro!=="falliti" && (
        <div style={{display:"flex",gap:7,background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:8,padding:"8px 12px",marginBottom:10,fontSize:12,color:"#991b1b"}}>
          <AlertTriangle size={14} style={{flexShrink:0,marginTop:1}}/>
          <div>{nFalliti} invi{nFalliti===1?"o non è andato":"i non sono andati"} a buon fine — {nFalliti===1?"va":"vanno"} verificat{nFalliti===1?"o":"i"}: chi doveva essere avvisato non lo è stato.</div>
        </div>
      )}

      {filtrati.length === 0 ? (
        <div style={{textAlign:"center",padding:28,color:"#9ca3af",fontSize:12.5}}>
          {log.length===0 ? "Nessun invio registrato — le email automatiche partiranno dopo l'attivazione delle Cloud Functions." : "Nessun invio con questo filtro."}
        </div>
      ) : (
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead>
              <tr style={{background:"#f9fafb"}}>
                {["Quando","Destinatario","Tipo","Riferimento","Esito"].map(h=>(
                  <th key={h} style={{padding:"7px 10px",textAlign:"left",fontSize:11,fontWeight:600,color:"#6b7280"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrati.map(l=>{
                const t = TIPI[l.tipo] || TIPI.generico;
                const ok = l.esito === "inviata";
                return (
                  <tr key={l.id} style={{borderTop:"1px solid #f3f4f6"}}>
                    <td style={{padding:"7px 10px",color:"#6b7280",whiteSpace:"nowrap"}}>
                      {l.inviataIl?.toDate?.()?.toLocaleString("it-IT",{day:"2-digit",month:"2-digit",year:"2-digit",hour:"2-digit",minute:"2-digit"}) || "—"}
                    </td>
                    <td style={{padding:"7px 10px",color:"#111827"}}>{l.destinatario||"—"}</td>
                    <td style={{padding:"7px 10px",color:"#6b7280",whiteSpace:"nowrap"}}>{t.emoji} {t.label}</td>
                    <td style={{padding:"7px 10px",color:"#6b7280"}}>{l.riferimentoNome||"—"}</td>
                    <td style={{padding:"7px 10px",whiteSpace:"nowrap"}}>
                      {ok ? (
                        <span style={{display:"inline-flex",alignItems:"center",gap:4,color:"#059669",fontWeight:700,fontSize:11}}>
                          <CheckCircle2 size={12}/> Inviata
                        </span>
                      ) : (
                        <span title={l.errore||""} style={{display:"inline-flex",alignItems:"center",gap:4,color:"#dc2626",fontWeight:700,fontSize:11,cursor:"help"}}>
                          <AlertTriangle size={12}/> Fallita
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
